import {ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../../../../Services/data.service';
import {StorageService} from '../../../../Services/storage.service';
import {Title} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {ReportPrintService} from '../../../../Services/report-print.service';
import {SendEmailModalComponent} from '../../../common/send-email-modal/send-email-modal.component';
import {EmailModel} from '../../../../DataModels/email-model';
import {Modal} from 'bootstrap';
import {DateFilterService} from '../../../../Services/dateFilter.service';

export interface CrmWonLostRow {
  opportunity_id: string;
  title: string;
  entity_name: string;
  contact_name: string;
  salesperson_id: string;
  opportunity_type: string;
  source: string;
  stage_name: string;
  estimated_value: number;
  expected_close_date: string;
  actual_close_date: string;
  status: string;
  lost_reason: string | null;
}

export interface CrmWonLostSalespersonGroup {
  salesperson_id: string;
  rows: CrmWonLostRow[];
  wonCount: number;
  lostCount: number;
  winRate: number;
  totalValueWon: number;
}

export interface CrmWonLostStatusGroup {
  status: string;
  salespersonGroups: CrmWonLostSalespersonGroup[];
}

@Component({
  selector: 'app-crm-won-lost-report',
  templateUrl: './crm-won-lost-report.component.html',
  styleUrls: [],
  standalone: false,
})
export class CrmWonLostReportComponent implements OnInit {

  @ViewChild('ReportOutput', {static: false}) ReportOutput: ElementRef;
  @ViewChild(SendEmailModalComponent, {read: ElementRef}) private localSendEmailModalHost: ElementRef;

  rptName = 'Won / Lost Analysis';
  isLoading = false;
  noDataFound = false;
  rows: CrmWonLostRow[] = [];
  statusGroups: CrmWonLostStatusGroup[] = [];
  users: any[] = [];
  reportDataToSendInEmail: EmailModel = new EmailModel();

  buttonConfig: any = {
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: false,
    close: true,
    clear: false,
    filters: false,
    reset: true,
  };

  filters: any = {
    company_id: '',
    salesperson_id: '',
    opportunity_type: '',
    from_date: '',
    to_date: '',
  };

  opportunityTypeOptions = [
    {value: '', label: '— All —'},
    {value: 'new_business', label: 'New Business'},
    {value: 'upsell', label: 'Upsell'},
    {value: 'renewal', label: 'Renewal'},
    {value: 'vendor_negotiation', label: 'Vendor Negotiation'},
  ];

  vm: any = {Preferences: []};

  constructor(
    private titleService: Title,
    private router: Router,
    private tableDataService: DataService,
    private localStorage: StorageService,
    private notification: BsToastService,
    private reportPrint: ReportPrintService,
    private cd: ChangeDetectorRef,
    private dateFilter: DateFilterService,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle(this.rptName);
    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences'));
    this.filters.company_id = this.vm.Preferences?.defaults?.company_id ?? '';

    this.tableDataService.getApiListData('sys_user', '?pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.users = result.Data ?? result.items ?? [];
      },
    });
  }

  getUserName(userId: string): string {
    if (!userId) {
      return '—';
    }
    const u = this.users.find(x => x.id === userId);
    return u ? u.name : userId;
  }

  statusClass(status: string): string {
    if (status === 'won') {
      return 'badge bg-success';
    }
    if (status === 'lost') {
      return 'badge bg-danger';
    }
    return 'badge bg-secondary';
  }

  resetFilters() {
    this.filters = {
      company_id: this.vm.Preferences?.defaults?.company_id ?? '',
      salesperson_id: '',
      opportunity_type: '',
      from_date: '',
      to_date: '',
    };
    this.rows = [];
    this.statusGroups = [];
    this.vm.report = null;
  }

  close() {
    this.router.navigate(['/home']);
  }

  runReport() {
    this.isLoading = true;
    this.noDataFound = false;
    this.rows = [];
    this.statusGroups = [];

    let params = 'company_id=' + (this.filters.company_id || '');
    if (this.filters.salesperson_id) {
      params += '&salesperson_id=' + this.filters.salesperson_id;
    }
    if (this.filters.opportunity_type) {
      params += '&opportunity_type=' + this.filters.opportunity_type;
    }
    const fd = this.dateFilter.GetDateFormat(this.filters.from_date);
    const td = this.dateFilter.GetDateFormat(this.filters.to_date);
    if (fd) {
      params += '&from_date=' + fd;
    }
    if (td) {
      params += '&to_date=' + td;
    }

    this.tableDataService.getDataWithParams('CrmWonLostReport', params).subscribe({
      next: (result: any) => {
        this.rows = Array.isArray(result) ? result : (result.Data ?? result.items ?? []);
        this.buildGroups();
        this.noDataFound = this.rows.length === 0;
        this.vm.report = this.rows;
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.notification.showErrorToast('Error loading report.');
        this.isLoading = false;
      },
    });
  }

  private buildGroups() {
    const statusOrder = ['won', 'lost', 'abandoned'];
    const statusMap = new Map<string, Map<string, CrmWonLostRow[]>>();
    for (const r of this.rows) {
      if (!statusMap.has(r.status)) {
        statusMap.set(r.status, new Map());
      }
      const spMap = statusMap.get(r.status)!;
      if (!spMap.has(r.salesperson_id)) {
        spMap.set(r.salesperson_id, []);
      }
      spMap.get(r.salesperson_id)!.push(r);
    }
    this.statusGroups = statusOrder
      .filter(s => statusMap.has(s))
      .map(s => {
        const spMap = statusMap.get(s)!;
        const salespersonGroups: CrmWonLostSalespersonGroup[] = Array.from(spMap.entries()).map(([spId, rows]) => {
          const wonCount = rows.filter(r => r.status === 'won').length;
          const lostCount = rows.filter(r => r.status === 'lost').length;
          const total = wonCount + lostCount;
          return {
            salesperson_id: spId,
            rows,
            wonCount,
            lostCount,
            winRate: total > 0 ? Math.round((wonCount / total) * 100) : 0,
            totalValueWon: rows.filter(r => r.status === 'won').reduce((sum, r) => sum + (r.estimated_value ?? 0), 0),
          };
        });
        return {status: s, salespersonGroups};
      });
  }

  printReport() {
    try {
      const html = this.buildPrintHtml();
      this.reportPrint.printInIframe(html);
    } catch (e) {
      console.error('CRM Won/Lost Report print failed', e);
    }
  }

  setEmailData() {
    const emailDocObj = {} as EmailModel;
    emailDocObj.print_orientation = '1';
    emailDocObj.convert_attachment_to = 'pdf';
    emailDocObj.fromEmail = '';
    emailDocObj.fromName = 'AcctXErp';
    emailDocObj.header_id = '';
    emailDocObj.toEmail = '';
    emailDocObj.body = 'Please see the attached ' + this.rptName + '.<br><br>Best regards,<br><br>AcctXErp<br><br>';
    emailDocObj.Subject = 'AcctXErp ' + this.rptName;
    try {
      this.cd.detectChanges();
    } catch {
    }
    try {
      emailDocObj.pdfHtml = this.buildPrintHtml(true);
    } catch {
      emailDocObj.pdfHtml = this.ReportOutput?.nativeElement?.outerHTML || '';
    }
    emailDocObj.activity_message = this.rptName;
    const now = new Date();
    emailDocObj.FileName = `CrmWonLostReport_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.pdf`;
    this.reportDataToSendInEmail = emailDocObj;
    const element = document.getElementById('sendEmailModal') as HTMLElement;
    if (element) {
      const emailModal = new Modal(element);
      emailModal.show();
    }
  }

  private buildPrintHtml(forEmail: boolean = false): string {
    const rawContent = (this.ReportOutput?.nativeElement?.outerHTML || '').toString();
    const content = this.reportPrint.cleanContentHtml(rawContent);
    const meta: { label: string; value: string }[] = [];
    return this.reportPrint.buildHtml({title: this.rptName, contentHtml: content, meta, orientation: 'landscape'});
  }
}
